import API from "@/api";

const parseSession = session => ({
  ...session.user,
  token: session.token,
  license: session.license,
  organization: session.organization,
  permissions: session.permissions || [],
  roles: session.roles || []
});

const emptyState = () => ({
  id: null,
  unique: null,
  name: null,
  email: null,
  admin: null,
  verified: null,
  trial_used: null,
  tfa_enabled: null,
  meta: null,
  token: {},
  settings: null,
  license: null,
  organization: null,
  customer_type: null,
  friend_code: null,
  require_2fa: false,
  require_password_change: false,
  parental_controls_enabled: false,
  permissions: [],
  roles: []
});

const state = {
  ...emptyState(),
  token: JSON.parse(localStorage.getItem("user-token")) || {}
};

const getters = {
  activeLicense: state => state.license,
  customerType: state => state.customer_type,

  hasPermission: state => permission => {
    return state.permissions.includes(permission);
  },

  hasRole: state => role => {
    return state.roles.includes(role);
  },

  hasUnupgradedTrial(state) {
    if (!state.id) return false;
    const { type, upgraded_to } = state.license;
    return type === "trial" && !upgraded_to;
  },

  licenseDaysLeft(state) {
    if (!state.id) return 0;
    const { expired_time } = state.license;
    const diff = new Date(expired_time) - new Date();
    return Math.ceil(diff / 86400000); // days
  },

  onPremQuickdropUrl: state => {
    if (!state.id) return null;
    const { data } = state.license;
    const url = data && data.config && data.config["quickdrop-url"];
    return url || null;
  },

  org: state => state.organization
};

export const mutations = {
  PATCH_USER(state, payload) {
    Object.assign(state, payload);
  }
};

export const actions = {
  async register({ commit }, data) {
    commit("REQUEST_INCREMENT", null, { root: true });

    const { success, results } = await API.register(data);

    if (success) {
      commit("PATCH_USER", parseSession(results));
      commit("UPDATE_TOKEN", results.token, { root: true });
    }

    commit("REQUEST_DECREMENT", null, { root: true });
  },

  async verify({ commit }, token) {
    commit("REQUEST_INCREMENT", null, { root: true });

    const { success } = await API.verify(token);
    if (success) {
      commit("PATCH_USER", { verified: true });
    }

    commit("REQUEST_DECREMENT", null, { root: true });
  },

  async login({ commit }, { login, pass }) {
    commit("REQUEST_INCREMENT", null, { root: true });

    const res = await API.login({ login, pass });
    const { success, results } = res;

    if (success) {
      commit("PATCH_USER", parseSession(results));

      if (results.token) {
        commit("UPDATE_TOKEN", results.token, { root: true });
      }
    } else {
      localStorage.removeItem("user-token"); // clear your user's token from localstorage
    }

    commit("REQUEST_DECREMENT", null, { root: true });
    return res;
  },

  async restoreSession({ commit }) {
    commit("REQUEST_INCREMENT", null, { root: true });
    const { success, results } = await API.getSession();

    if (success) {
      commit("PATCH_USER", parseSession(results));
      commit("UPDATE_TOKEN", results.token, { root: true });
      document.cookie = `nanotoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } else {
      localStorage.removeItem("user-token"); // clear your user's token from localstorage
    }

    commit("REQUEST_DECREMENT", null, { root: true });
  },

  async logout({ commit }) {
    commit("REQUEST_INCREMENT", null, { root: true });

    await API.logout();

    commit("PATCH_USER", emptyState());
    commit("UPDATE_ADMIN", null, { root: true });

    localStorage.removeItem("user-token");

    commit("REQUEST_DECREMENT", null, { root: true });
  },

  async logoutOthers({ commit }) {
    commit("REQUEST_INCREMENT", null, { root: true });
    const res = await API.logoutOthers();
    commit("REQUEST_DECREMENT", null, { root: true });
    return res.success;
  },

  async disable2FA({ commit }) {
    commit("REQUEST_INCREMENT", null, { root: true });

    const { success } = await API.disable2FA();
    if (success) commit("PATCH_USER", { tfa_enabled: false });

    commit("REQUEST_DECREMENT", null, { root: true });
    return success;
  },

  async edit({ commit, state }, { admin, pass, data }) {
    commit("REQUEST_INCREMENT", null, { root: true });

    let res;
    if (admin) {
      res = await API.setUserPassword({
        user_id: state.id,
        password: data.pass
      });
    } else {
      res = await API.editUser({ pass, data });
    }
    commit("PATCH_USER", data);

    commit("REQUEST_DECREMENT", null, { root: true });
    return res.success;
  },

  async editMeta({ commit }, meta) {
    commit("REQUEST_INCREMENT", null, { root: true });

    const { success } = await API.editUserMeta(meta);
    commit("PATCH_USER", { meta });

    commit("REQUEST_DECREMENT", null, { root: true });
    return success;
  },

  async editSettings({ commit }, settings) {
    commit("REQUEST_INCREMENT", null, { root: true });

    const { success } = await API.editUserSettings(settings);
    commit("PATCH_USER", { settings });

    commit("REQUEST_DECREMENT", null, { root: true });
    return success;
  },

  async resetPassword({ commit }, { token, pass }) {
    commit("REQUEST_INCREMENT", null, { root: true });

    const { success, results } = await API.resetPassword({ token, pass });
    if (success) {
      commit("PATCH_USER", parseSession(results));
      commit("UPDATE_TOKEN", results.token, { root: true });
    }

    commit("REQUEST_DECREMENT", null, { root: true });
  },

  async startTrial({ commit }) {
    commit("REQUEST_INCREMENT", null, { root: true });

    await API.startTrial();

    commit("REQUEST_DECREMENT", null, { root: true });
    location.reload();
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
